import { useCallback, useEffect, useState } from "react";
import { useUrlState } from "@/lib/navState";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Gauge, HardHat, Building2, Sliders, Trophy, AlertTriangle, ImageIcon, ChevronRight, Save } from "lucide-react";

const MGMT = ["Owner", "Admin", "GM", "Branch Manager"];
const PERIODS = [["today", "Hari"], ["week", "Minggu"], ["month", "Bulan"]];
const STATUS = {
  EXCELLENT: ["EXCELLENT", "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30"],
  GOOD: ["GOOD", "bg-blue-500/15 text-blue-300 ring-blue-400/30"],
  NEED_ATTENTION: ["NEED ATTENTION", "bg-amber-500/15 text-amber-300 ring-amber-400/30"],
  IMPROVEMENT_REQUIRED: ["IMPROVEMENT", "bg-red-500/15 text-red-300 ring-red-400/30"],
  DATA_INCOMPLETE: ["DATA INCOMPLETE", "bg-white/10 text-[#8A98B0] ring-white/15"],
};

function Shell({ children }) {
  return (
    <div className="ti-shell -mx-3 -my-3 min-h-[calc(100vh-3.25rem)] px-3 py-5 sm:-mx-5 sm:-my-5 sm:px-6 sm:py-6">
      <div className="mx-auto w-full max-w-[1500px]">{children}</div>
    </div>
  );
}
function StatusPill({ s }) {
  const [t, c] = STATUS[s] || STATUS.DATA_INCOMPLETE;
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ring-1 ring-inset ${c}`}>{t}</span>;
}
function Card({ label, value, sub }) {
  return <div className="ti-card p-3"><div className="text-[10px] font-semibold uppercase tracking-wider ti-muted">{label}</div><div className="mt-1 font-heading text-xl font-bold text-white">{value}</div>{sub && <div className="text-[11px] ti-muted">{sub}</div>}</div>;
}

function DetailModal({ uid, scheme, period, demo, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/kpi/employee/${uid}`, { params: { scheme, period, demo } }).then((r) => setD(r.data)).catch(() => setD({ found: false }));
  }, [uid, scheme, period, demo]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-blue-500/25 bg-[#0A1220] text-[#E7ECF3] sm:max-w-2xl">
        <DialogHeader><DialogTitle className="text-white">{d?.name || uid} · <span className="ti-muted text-sm">{d?.role}</span></DialogTitle><DialogDescription className="sr-only">Detail KPI karyawan, metrik target vs actual, dan bukti pekerjaan.</DialogDescription></DialogHeader>
        {!d && <p className="py-8 text-center ti-muted">Memuat…</p>}
        {d && d.found === false && <p className="py-8 text-center ti-muted">Tidak ada data pada periode ini.</p>}
        {d && d.found !== false && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="ti-card flex-1 p-3 text-center"><div className="font-heading text-3xl font-extrabold text-[#60A5FA]">{d.kpi_score}</div><div className="text-[10px] uppercase ti-muted">KPI Score</div></div>
              <div className="flex-1"><StatusPill s={d.status} /></div>
            </div>
            <div>
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wider ti-muted">Metrik · Target vs Actual</div>
              <div className="overflow-hidden rounded-lg border border-blue-500/15">
                <table className="w-full text-left text-sm">
                  <thead className="bg-white/5 text-[10px] uppercase ti-muted"><tr><th className="px-2 py-1.5">Metric</th><th className="px-2 py-1.5 text-right">Target</th><th className="px-2 py-1.5 text-right">Actual</th><th className="px-2 py-1.5 text-right">Achv%</th><th className="px-2 py-1.5 text-right">Bobot</th><th className="px-2 py-1.5 text-right">Score</th></tr></thead>
                  <tbody>
                    {(d.metrics || []).map((m) => (
                      <tr key={m.key} className="border-t border-blue-500/10">
                        <td className="px-2 py-1.5 text-[#C7D2E5]">{m.label}</td>
                        {m.incomplete ? <td colSpan={3} className="px-2 py-1.5 text-center text-[11px] font-semibold text-amber-400">DATA INCOMPLETE</td> : (
                          <><td className="px-2 py-1.5 text-right text-[#C7D2E5]">{m.target}</td><td className="px-2 py-1.5 text-right text-white">{m.actual}</td><td className="px-2 py-1.5 text-right font-bold text-[#60A5FA]">{m.achievement}%</td></>
                        )}
                        <td className="px-2 py-1.5 text-right ti-muted">{m.weight}%</td>
                        <td className="px-2 py-1.5 text-right font-semibold text-white">{m.incomplete ? "—" : m.score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {d.incentive && (
              <div className="ti-card p-3">
                <div className="mb-1 text-[11px] font-bold uppercase tracking-wider ti-muted">Incentive Readiness</div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="font-heading text-lg font-bold text-white">{d.incentive.kpi_score ?? d.kpi_score}</div><div className="text-[9px] uppercase ti-muted">KPI</div></div>
                  <div><div className="font-heading text-lg font-bold text-emerald-400">{d.incentive.approved_output ?? "—"}</div><div className="text-[9px] uppercase ti-muted">Approved Output</div></div>
                  <div><div className="font-heading text-lg font-bold text-[#60A5FA]">{d.incentive.eligible_jobs ?? "—"}</div><div className="text-[9px] uppercase ti-muted">Eligible Jobs</div></div>
                </div>
                <div className="mt-2 rounded bg-amber-500/10 px-2 py-1 text-center text-[11px] font-semibold text-amber-400">{d.incentive.rule}</div>
              </div>
            )}
            {(d.jobs || []).length > 0 && (
              <div>
                <div className="mb-1 text-[11px] font-bold uppercase tracking-wider ti-muted">Evidence / Drill-down (WO · Nopol · Job · Foto · QC)</div>
                <div className="space-y-1.5">
                  {d.jobs.map((j, i) => (
                    <div key={i} className="ti-card flex flex-wrap items-center gap-2 p-2 text-xs">
                      <span className="font-mono font-bold text-[#60A5FA]">{j.wo || "WO—"}</span>
                      <span className="ti-plate text-[10px]">{j.nopol || "—"}</span>
                      <span className="min-w-0 flex-1 truncate text-[#C7D2E5]">{j.job} · {j.panel}</span>
                      <span className="flex items-center gap-1 ti-muted"><ImageIcon size={11} />{j.before}/{j.after}</span>
                      {j.rework > 0 && <span className="text-amber-400">RW{j.rework}</span>}
                      <span className="font-semibold text-white">{j.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WeightsEditor({ scheme, canEdit }) {
  const [w, setW] = useState({});
  const [labels, setLabels] = useState({});
  const load = useCallback(async () => {
    const { data } = await api.get("/kpi/config");
    setW(data[scheme].weights); setLabels(data[scheme].labels);
  }, [scheme]);
  useEffect(() => { load(); }, [load]);
  const total = Object.values(w).reduce((a, v) => a + Number(v || 0), 0);
  const save = async () => {
    try { await api.put("/kpi/config", { scheme, weights: w }); toast.success("Bobot disimpan"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
  };
  return (
    <div className="ti-card p-4">
      <div className="flex items-center gap-2"><Sliders size={15} className="text-[#60A5FA]" /><h3 className="font-heading text-sm font-bold text-white">Bobot {scheme === "technical" ? "Technical" : "Admin"}</h3></div>
      <div className="mt-3 space-y-2">
        {Object.keys(labels).map((k) => (
          <div key={k} className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-sm text-[#C7D2E5]">{labels[k]}</span>
            <input data-testid={`weight-${scheme}-${k}`} type="number" min="0" max="100" disabled={!canEdit} value={w[k] ?? 0}
              onChange={(e) => setW((o) => ({ ...o, [k]: Number(e.target.value) }))}
              className="h-8 w-20 rounded border border-blue-500/25 bg-[#0A1424] px-2 text-right text-sm text-white disabled:opacity-60" />
            <span className="w-4 ti-muted">%</span>
          </div>
        ))}
      </div>
      <div className={`mt-3 flex items-center justify-between rounded-lg px-3 py-2 text-sm font-bold ${Math.abs(total - 100) < 0.01 ? "bg-emerald-500/10 text-emerald-300" : "bg-red-500/10 text-red-300"}`}>
        <span>Total</span><span data-testid={`weight-total-${scheme}`}>{total}%</span>
      </div>
      {canEdit && <Button data-testid={`weight-save-${scheme}`} onClick={save} disabled={Math.abs(total - 100) >= 0.01} className="mt-3 w-full ti-btn-primary"><Save size={14} className="mr-1" />Simpan Bobot</Button>}
      {!canEdit && <p className="mt-2 text-[11px] ti-muted">Hanya Owner/Admin yang dapat mengubah bobot.</p>}
    </div>
  );
}

function ScoreBoard({ scheme, period, demo }) {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [otherAvg, setOtherAvg] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/kpi/scores", { params: { scheme, period, demo } }); setData(data); } catch (e) { setData({ rows: [], summary: {} }); }
    try { const other = scheme === "technical" ? "admin" : "technical"; const r = await api.get("/kpi/scores", { params: { scheme: other, period, demo } }); setOtherAvg({ scheme: other, avg: r.data.summary?.avg ?? 0 }); } catch (e) { /* ignore */ }
  }, [scheme, period, demo]);
  useEffect(() => { load(); }, [load]);
  const s = data.summary || {};
  const techAvg = scheme === "technical" ? s.avg : otherAvg?.avg;
  const adminAvg = scheme === "admin" ? s.avg : otherAvg?.avg;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
        <Card label="Total Employee" value={s.total_employee ?? 0} />
        <Card label="Admin KPI Avg" value={adminAvg ?? "—"} />
        <Card label="Technical KPI Avg" value={techAvg ?? "—"} />
        <Card label="Top Performer" value={s.top?.name || "—"} sub={s.top ? `${s.top.score}` : ""} />
        <Card label="Low KPI" value={s.low?.name || "—"} sub={s.low ? `${s.low.score}` : ""} />
        <Card label="Completed Jobs" value={s.completed_jobs ?? 0} />
        <Card label="Rework" value={s.rework ?? 0} />
        <Card label="Window" value={data.window?.from?.slice(5) || "—"} sub={data.window?.to?.slice(5)} />
      </div>
      <div className="ti-card overflow-hidden">
        <div className="hidden bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider ti-muted lg:grid lg:grid-cols-[minmax(0,1fr)_120px_90px_90px_120px_28px] lg:gap-2">
          <span>Nama</span><span>Role</span><span className="text-right">Output</span><span className="text-right">KPI Score</span><span className="text-right">Status</span><span></span>
        </div>
        {(data.rows || []).map((r) => (
          <button key={r.user_id} data-testid={`kpi-rank-${r.user_id}`} onClick={() => setDetail(r.user_id)}
            className="flex w-full items-center gap-2 border-b border-blue-500/10 px-3 py-2.5 text-left transition-colors hover:bg-white/5 lg:grid lg:grid-cols-[minmax(0,1fr)_120px_90px_90px_120px_28px]">
            <span className="flex min-w-0 flex-1 items-center gap-2 font-semibold text-white">{scheme === "technical" ? <HardHat size={14} className="shrink-0 text-[#60A5FA]" /> : <Building2 size={14} className="shrink-0 text-[#60A5FA]" />}<span className="truncate">{r.name}</span></span>
            <span className="text-sm ti-muted">{r.role}</span>
            <span className="text-right text-sm text-[#C7D2E5]">{r.approved_output ?? "—"}</span>
            <span className="text-right font-heading text-base font-bold text-[#60A5FA]">{r.status === "DATA_INCOMPLETE" ? "—" : r.kpi_score}</span>
            <span className="flex justify-end"><StatusPill s={r.status} /></span>
            <ChevronRight size={15} className="ml-auto ti-muted" />
          </button>
        ))}
        {(data.rows || []).length === 0 && <p className="py-10 text-center text-sm ti-muted">Belum ada data pada periode ini.</p>}
      </div>
      {detail && <DetailModal uid={detail} scheme={scheme} period={period} demo={demo} onClose={() => setDetail(null)} />}
    </div>
  );
}

export default function KPIPerformance() {
  const { user } = useAuth();
  const isMgmt = user?.role === "admin" || MGMT.includes(user?.role_name);
  const isTechOnly = user?.role_name === "Technician" && !isMgmt;
  const canAdminView = isMgmt || ["Finance", "Service Advisor", "Kepala Bengkel", "QC"].includes(user?.role_name);
  const [period, setPeriod] = useState("month");
  const [demo, setDemo] = useState(false);
  const [tab, setTab] = useUrlState("tab", "tech", true);
  const [selfDetail, setSelfDetail] = useState(false);

  if (isTechOnly) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#60A5FA]"><Gauge size={13} /> Performance</div>
        <h1 className="mt-1 font-heading text-2xl font-extrabold text-white">KPI Saya</h1>
        <p className="mt-1 text-sm ti-muted">Skor & bukti pekerjaan Anda (dihitung dari data job, Anda tidak dapat input nilai sendiri).</p>
        <div className="mt-3 flex gap-1.5">{PERIODS.map(([k, l]) => <button key={k} data-testid={`kpi-period-${k}`} onClick={() => setPeriod(k)} className={`rounded-full px-3 py-1 text-xs font-semibold ${period === k ? "ti-gold-bg text-[#04101F]" : "ti-card ti-muted"}`}>{l}</button>)}</div>
        <div className="mt-4"><Button data-testid="kpi-self-open" onClick={() => setSelfDetail(true)} className="ti-btn-primary">Lihat Detail KPI Saya</Button></div>
        {selfDetail && <DetailModal uid={user?.user_id} scheme="technical" period={period} demo={false} onClose={() => setSelfDetail(false)} />}
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#60A5FA]"><Gauge size={13} /> Employee KPI V1</div>
          <h1 className="mt-1 font-heading text-2xl font-extrabold text-white sm:text-3xl">KPI &amp; Performance</h1>
          <p className="mt-1 text-sm ti-muted">Target vs Actual · skor berbobot dari data operasional real (tanpa dummy).</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {PERIODS.map(([k, l]) => <button key={k} data-testid={`kpi-period-${k}`} onClick={() => setPeriod(k)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${period === k ? "ti-gold-bg text-[#04101F]" : "ti-card ti-muted"}`}>{l}</button>)}
          <button data-testid="kpi-demo-toggle" onClick={() => setDemo((v) => !v)} className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${demo ? "bg-amber-500/15 text-amber-300 ring-amber-400/40" : "ti-card ti-muted ring-transparent"}`}>{demo ? "Demo Mode" : "Production"}</button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid="kpi-tab-tech" onClick={() => setTab("tech")} className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "tech" ? "bg-blue-500/15 text-white ring-1 ring-inset ring-blue-400/50" : "text-[#9FADC6] hover:bg-white/5"}`}><HardHat size={15} /> Technical</button>
        {canAdminView && <button data-testid="kpi-tab-admin" onClick={() => setTab("admin")} className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "admin" ? "bg-blue-500/15 text-white ring-1 ring-inset ring-blue-400/50" : "text-[#9FADC6] hover:bg-white/5"}`}><Building2 size={15} /> Admin / Office</button>}
        <button data-testid="kpi-tab-config" onClick={() => setTab("config")} className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "config" ? "bg-blue-500/15 text-white ring-1 ring-inset ring-blue-400/50" : "text-[#9FADC6] hover:bg-white/5"}`}><Sliders size={15} /> Bobot KPI</button>
      </div>

      <div className="mt-4">
        {tab === "config" ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <WeightsEditor scheme="technical" canEdit={isMgmt} />
            <WeightsEditor scheme="admin" canEdit={isMgmt} />
          </div>
        ) : tab === "admin" && canAdminView ? (
          <ScoreBoard scheme="admin" period={period} demo={demo} />
        ) : (
          <ScoreBoard scheme="technical" period={period} demo={demo} />
        )}
      </div>
    </Shell>
  );
}
